/**
 * COGS tab — new in v2.
 *
 * Direct Wages is computed, not editable: it comes from the Direct Staff
 * department on the Staffing tab (headcount x cost per head). Making it
 * editable here would give two places to change one number.
 *
 * Cost of Sales is signed negative, and INCLUDES direct wages — see spec 7.1.
 * The template charged direct labour nowhere, which is the correction this tab
 * makes visible.
 */

import { useCallback } from 'react';
import { ComputedDataRow } from '../table/DataRow';
import { V2EditableRow } from './V2EditableRow';
import { GrandTotalRow } from '../table/GrandTotalRow';
import { SectionHeader } from '../table/SectionHeader';
import { SubtotalRow } from '../table/SubtotalRow';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import { V2Sheet } from './V2Table';

export function CogsView() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateInput = useFinancialModelV2Store((s) => s.updateInput);

  const handleChange = useCallback(
    (path: string) => (period: string, value: number) => updateInput(path, period, value),
    [updateInput],
  );

  if (!model) return null;
  const { inputs, computed } = model;

  return (
    <V2Sheet>
      <SectionHeader label="Stock" />
      <V2EditableRow
        label="Opening Stock"
        data={inputs.cogs.openingStock}
        onCellChange={handleChange('cogs.openingStock')}
      />
      {/* The figure in use: a blank year falls back to minus goods sold (no stock movement), and
          an entry replaces it for that year - which is what models stock. */}
      <V2EditableRow
        label="Goods Purchased"
        data={computed.cogs.goodsPurchased}
        onCellChange={handleChange('cogs.goodsPurchased')}
      />
      {/* Shows the figures the engine used: blank forecast years are filled at
          the last historical year's % of turnover, and typing a value overrides. */}
      <V2EditableRow
        label="Goods Sold (negative; blank forecast = last historical % of sales)"
        data={computed.cogs.goodsSold}
        onCellChange={handleChange('cogs.goodsSold')}
      />
      <V2EditableRow
        label="Inventory written down (positive)"
        data={inputs.cogs.inventoryWriteDown ?? {}}
        onCellChange={handleChange('cogs.inventoryWriteDown')}
      />
      <SubtotalRow label="Closing Stock" data={computed.cogs.closingStock} />

      <SectionHeader label="Direct Labour" />
      <ComputedDataRow label="Direct Wages (from Staffing)" data={computed.cogs.directWages} />

      <GrandTotalRow label="Cost of Sales" data={computed.cogs.costOfSales} />
    </V2Sheet>
  );
}
